import React, { ReactNode, useEffect, useState } from 'react';
import { Actions, MultiInputControls } from 'pages/components/form/MultiInput/MultiInputControls';
import { IntlShape, useIntl } from 'react-intl';
import { JustifiedDeleteButton } from '../../../components/form/MultiInput/Buttons';
import { ValidationResult, ValidationResults } from '../../../../util/validation/interfaces';
import { FloatLabel } from 'primereact/floatlabel';
import Label from '../../../components/Label';
import { CheckPermissionFunction } from '../../../../Auth/components/PermissionGate';
import {
  ProjectFundingReferenceDTO,
  ProjectFundingReferenceExternalIdentifierDTO,
  ProjectFundingReferenceFundingStreamDTO,
  ProjectFundingReferenceFundingSubstreamDTO, WorkGeoLocationDTO,
} from '../../../../api/core';
import {
  getDefaultFundingStream,
  getDefaultFundingSubstream,
  getDefaultProjectFundingRef, getDefaultProjectFundingReferenceExternalIdentifier,
} from '../../../../Settings/emptyModels';
import { getVocabularyOptions } from '../../../../Vocabulary/util';
import { Chips, ChipsChangeEvent } from 'primereact/chips';
import { LabelledInputText } from '../../../components/form/LabelledInputText';
import { LabelledDropdown } from '../../../components/form/LabelledDropdown';
import { Panel } from '../layout/Panel';
import { Fieldset } from '../layout/Fieldset';
import { SelectItem } from 'primereact/selectitem';
import { InputGroup } from '../layout/InputGroup';

interface ProjectFundingReferencesProps {
  label: string;
  checkPermission: CheckPermissionFunction;
  projectFundingReferences: ProjectFundingReferenceDTO[];
  onChange: (referencedOrganisations: ProjectFundingReferenceDTO[]) => void;
  validation: ValidationResult;
  autoFocus: boolean;
}

interface ProjectFundingReferenceProps {
  idx: number;
  label: string;
  checkPermission: CheckPermissionFunction;
  projectFundingReference: ProjectFundingReferenceDTO;
  onChange: (referencedOrganisations: ProjectFundingReferenceDTO) => void;
  autoFocus: boolean;
  validation: ValidationResults;
  delete: (e: React.MouseEvent<any>) => void;
}

export function ProjectFundingReferences(props: ProjectFundingReferencesProps): ReactNode {
  return (
    <Panel title={props.label} toggleable={true}>
      <MultiInputControls<ProjectFundingReferenceDTO>
        lines={props.projectFundingReferences}
        onChange={props.onChange}
        canEdit={!!props.checkPermission().canWrite}
        renderLines={(
          reference: ProjectFundingReferenceDTO,
          onLineChange: (newValues: ProjectFundingReferenceDTO) => void,
          actions: Actions,
          idx: number
        ): ReactNode => (
          <ProjectFundingReference
            idx={idx}
            label={props.label}
            checkPermission={props.checkPermission}
            projectFundingReference={reference}
            onChange={onLineChange}
            validation={props.validation?.children[idx]}
            autoFocus={props.autoFocus}
            delete={actions.delete}
          />
        )}
        addNew={(lines: ProjectFundingReferenceDTO[]): number => lines.push(getDefaultProjectFundingRef())}
      />
    </Panel>
  );
}

function ProjectFundingReference(props: ProjectFundingReferenceProps) {
  const intl: IntlShape = useIntl();
  const [fundingStreams, setFundingStreams] = useState<SelectItem[]>([]);
  const [loaded, setLoaded] = useState<boolean>(false);

  const updateFundingStreamOptions = (): void => {
    if(!props.projectFundingReference.funderName && fundingStreams.length > 0){
      setFundingStreams([]);
    }
    const v: SelectItem[] = getVocabularyOptions(intl.locale, 'funding_stream_list', props.projectFundingReference.funderName) || [];
    setFundingStreams(v || []);
  };

  useEffect((): void => updateFundingStreamOptions(), []);

  useEffect((): void => updateFundingStreamOptions(), [props.projectFundingReference.funderName]);

  useEffect((): void => {
    if(loaded){
      props.onChange({
        ...props.projectFundingReference,
        projectFundingReferenceFundingStreams: (
          props.projectFundingReference.projectFundingReferenceFundingStreams || []
        ).filter((stream: ProjectFundingReferenceFundingStreamDTO): boolean =>
          (fundingStreams || []).some(
            (fundingStream: SelectItem): boolean => fundingStream.value === stream.fundingStream
          )
        ),
      });
    }
    setLoaded(true);
  },[fundingStreams]);

  return (
    <div className={'flex flex-column p-fluid'}>
      <Fieldset title={`${props.label}.attr.funder`}>
        <LabelledDropdown
          id={`funder-${props.idx}-name`}
          autoFocus={props.autoFocus}
          value={props.projectFundingReference.funderName}
          permissions={props.checkPermission('funder_name')}
          validation={props.validation?.fields?.funder_name}
          options={getVocabularyOptions(intl.locale, 'funder_list', '*')}
          onChange={(funderName: string): void => props.onChange({ ...props.projectFundingReference, funderName })}
          label={`${props.label}.attr.funderName`}
        />
        {(fundingStreams?.length > 0 ||
          (props.projectFundingReference.projectFundingReferenceFundingStreams || []).length > 0) && (
          <Fieldset title={`${props.label}.attr.fundingStreams`}>
            <MultiInputControls
              canEdit={props.checkPermission('fundingReferenceFundingStreams').canWrite}
              lines={props.projectFundingReference.projectFundingReferenceFundingStreams}
              addNew={(rows: ProjectFundingReferenceDTO[]): number => rows.push(getDefaultFundingStream())}
              onChange={(projectFundingReferenceFundingStreams: ProjectFundingReferenceFundingStreamDTO[]): void =>
                props.onChange({ ...props.projectFundingReference, projectFundingReferenceFundingStreams })
              }
              renderLines={(
                stream: ProjectFundingReferenceFundingStreamDTO,
                onLineChange: (newValues: ProjectFundingReferenceFundingStreamDTO) => void,
                actions: Actions,
                idx: number
              ): ReactNode => {
                return (
                  <InputGroup
                    delete={{
                      action: actions.delete,
                      disabled: !props.checkPermission().canWrite,
                    }}
                  >
                    <LabelledDropdown
                      value={stream.fundingStream}
                      onChange={(fundingStream: string): void => onLineChange({ ...stream, fundingStream })}
                      options={fundingStreams}
                      id={`fundingReferenceFundingStreams${idx}`}
                      autoFocus={props.autoFocus}
                      label={`${props.label}.attr.fundingStream`}
                      permissions={props.checkPermission('project_funding_reference_funding_streams.funding_stream')}
                      className={'border-round-left-md'}
                      validation={
                        props.validation?.fields?.project_funding_reference_funding_streams.children[idx].fields
                          .funding_stream
                      }
                    />
                  </InputGroup>
                );
              }}
            />
          </Fieldset>
        )}
        <Fieldset title={`${props.label}.attr.fundingSubstreams`}>
          <MultiInputControls
            canEdit={props.checkPermission('fundingReferenceFundingSubstreams').canWrite}
            lines={props.projectFundingReference.projectFundingReferenceFundingSubstreams}
            addNew={(rows: ProjectFundingReferenceDTO[]): number => rows.push(getDefaultFundingSubstream())}
            onChange={(projectFundingReferenceFundingSubstreams: ProjectFundingReferenceFundingSubstreamDTO[]): void =>
              props.onChange({ ...props.projectFundingReference, projectFundingReferenceFundingSubstreams })
            }
            renderLines={(
              substream: ProjectFundingReferenceFundingSubstreamDTO,
              onLineChange: (newValues: ProjectFundingReferenceFundingSubstreamDTO) => void,
              actions: Actions,
              idx: number
            ): ReactNode => {
              return (
                <InputGroup
                  delete={{
                    action: actions.delete,
                    disabled: !props.checkPermission().canWrite,
                  }}
                >
                  <LabelledInputText
                    value={substream.fundingSubstream}
                    onChange={(fundingSubstream: string): void => onLineChange({ ...substream, fundingSubstream })}
                    id={`fundingReferenceFundingSubstreams${idx}`}
                    autoFocus={props.autoFocus}
                    label={`${props.label}.attr.fundingSubstream`}
                    permissions={props.checkPermission(
                      'project_funding_reference_funding_substreams.funding_substream'
                    )}
                    className={'border-round-left-md'}
                    validation={
                      props.validation?.fields?.project_funding_reference_funding_substreams.children[idx].fields
                        .funding_substream
                    }
                  />
                </InputGroup>
              );
            }}
          />
        </Fieldset>
        <LabelledInputText
          id={`projectAcronym${props.idx}`}
          value={props.projectFundingReference.projectAcronym}
          permissions={props.checkPermission('project_acronym')}
          validation={props.validation?.fields?.project_acronym}
          onChange={(projectAcronym: string): void =>
            props.onChange({ ...props.projectFundingReference, projectAcronym })
          }
          label={`${props.label}.attr.projectAcronym`}
        />
        <Fieldset title={`${props.label}.attr.externalProjectIdentifiers`}>
          <MultiInputControls<ProjectFundingReferenceExternalIdentifierDTO>
            canEdit={props.checkPermission('external_project_identifiers').canWrite}
            lines={props.projectFundingReference.externalProjectIdentifiers || []}
            addNew={(rows: ProjectFundingReferenceExternalIdentifierDTO[]): number =>
              rows.push(getDefaultProjectFundingReferenceExternalIdentifier())
            }
            onChange={(externalProjectIdentifiers: ProjectFundingReferenceExternalIdentifierDTO[]): void =>
              props.onChange({ ...props.projectFundingReference, externalProjectIdentifiers })
            }
            renderLines={(
              externalIdentifierDTO: ProjectFundingReferenceExternalIdentifierDTO,
              onLineChange: (externalIdentifier: ProjectFundingReferenceExternalIdentifierDTO) => void,
              actions: Actions,
              idx: number
            ): ReactNode => {
              return (
                <InputGroup
                  key={`externalProjectIdentifiers${props.idx}`}
                  delete={{
                    disabled: !props.checkPermission('external_project_identifiers').canWrite,
                    action: actions.delete,
                  }}
                >
                  <LabelledInputText
                    id={`externalProjectIdentifiers${props.idx}externalProjectIdentifier${idx}`}
                    value={externalIdentifierDTO.identifier}
                    permissions={props.checkPermission('external_project_identifiers.identifier')}
                    validation={props.validation?.fields?.external_project_identifiers.children[idx].fields.identifier                   }
                    onChange={(identifier: string): void => onLineChange({ ...externalIdentifierDTO, identifier })}
                    label={`${props.label}.attr.externalProjectIdentifier`}
                  />
                </InputGroup>
              );
            }}
          />
        </Fieldset>
        <LabelledInputText
          id={`projectTitle${props.idx}`}
          value={props.projectFundingReference.projectTitle}
          permissions={props.checkPermission('project_title')}
          validation={props.validation?.fields?.project_title}
          onChange={(projectTitle: string): void => props.onChange({ ...props.projectFundingReference, projectTitle })}
          label={`${props.label}.attr.projectTitle`}
        />
        <LabelledInputText
          id={`projectUri${props.idx}`}
          value={props.projectFundingReference.projectUri}
          permissions={props.checkPermission('project_uri')}
          validation={props.validation?.fields?.project_uri}
          onChange={(projectUri: string): void => props.onChange({ ...props.projectFundingReference, projectUri })}
          label={`${props.label}.attr.projectUri`}
        />
        <JustifiedDeleteButton onClick={props.delete} disabled={!props.checkPermission().canWrite} />
      </Fieldset>
    </div>
  );
}
